import { getLocalVideoTrackForCallSetting, setLocalVideoTrackForCallSetting } from "../components/Twilio/Room/roomService";
import { createMyLocalVideoTrack, restartLocalVideoTracks } from "../components/Twilio/twilioCallUtility";
import { attachTrackInContainer } from "../components/Twilio/VideoCall/VideoUtils";
import reduxStore from "../configureEnvStore";
import {
    CAMERA_FACING_MODE,
    INPUT_OUTPUT_DEVICE_TYPE,
    MEDIA_DEVICE_PERMISSION_QUERY_TYPE,
    MEDIA_PERMISSIONS,
} from "../constants/CallSettingConstants";
import { PublicVariables } from "../constants/PublicVariables";
import Localize from "../locale/Localize";
import {
    setAudioDevices,
    setAudioPermission,
    setCameraDevices,
    setCameraPermission,
    setCurrentAudioDevice,
    setCurrentCameraDevice,
    setIsCameraExist,
    setIsFrontCameraSelected,
    setIsMicExist,
    setSpeakerDevices,
    setStartCallSettingLocalVideo,
} from "../reducers/call-setting-reducer";
import { setIsCallSettingDrawerOpen } from "../reducers/common-reducer";
import { setIsLocalVideoAttached } from "../reducers/twilioEvents-reducer";
import { micCameraPermissionDatatrackSend } from "../shared/dataTrackFunction";
import { checkDeviceHasCamIssue } from "../shared/utility";
import { LogFirebase } from "./firestore";
import flashService from "./flashService";

class MediaDeviceService {
    constructor() {
        this.isMicExist = false;
        this.isCameraExist = false;
        this.initialization = this.checkAvailableInputOutputDevices();
        this.audioScriptNode = null;
        this.allMediaDevicesList = [];
        this.stream = null;
    }

    // It will verify the presence of camera or audio devices in the system.
    async checkAvailableInputOutputDevices() {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            return this.categorizeMediaDevices(devices);
        } catch (err) {
            console.log("checkAvailableInputOutputDevices error ==>", err);
        }
    }

    // it will set audio for the first time based on precedence which is mention belowed
    getAudioSource(selectedDevice) {
        const audioDevices = this.allMediaDevicesList["audio"] || [];
        let selectedDeviceExist = false;
        let bluetoothDevice = null;
        let wiredHeadsetDevice = null;
        let speakerPhoneDevice = null;

        audioDevices.forEach((device) => {
            if (selectedDevice?.deviceId === device.deviceId) {
                selectedDeviceExist = true;
            }
            if (device.label.toLowerCase().indexOf("bluetooth") > -1) {
                bluetoothDevice = device;
            }
            if (device.label.toLowerCase().indexOf("wired") > -1) {
                wiredHeadsetDevice = device;
            }
            if (device.label.toLowerCase().indexOf("speakerphone") > -1) {
                speakerPhoneDevice = device;
            }
        });

        // Establish priority order: Prev Selected Device > Bluetooth > handsfree > speakerphone
        if (selectedDeviceExist) {
            return selectedDevice;
        } else if (bluetoothDevice) {
            return bluetoothDevice;
        } else if (wiredHeadsetDevice) {
            return wiredHeadsetDevice;
        } else if (speakerPhoneDevice) {
            return speakerPhoneDevice;
        }

        //assign selected object
        if (!selectedDeviceExist) {
            const inputVar = this.getAudioInputById(selectedDevice);
            if (inputVar) {
                return inputVar;
            }
        }

        return null; // In case no devices are available
    }

    /**return audio input object by device id */
    getAudioInputById = function (id) {
        let retVal = false;
        this.allMediaDevicesList.audio.forEach(function (device) {
            if (device.deviceId == id) {
                retVal = device;
            }
        });
        return retVal;
    };

    getVideoSource(currentCameraDevice) {
        if (currentCameraDevice?.deviceId) {
            return { deviceId: { exact: currentCameraDevice.deviceId } };
        }

        if (checkDeviceHasCamIssue()) {
            reduxStore.dispatch(setIsFrontCameraSelected(true));
            return { facingMode: CAMERA_FACING_MODE.FRONT }; // user
        }

        reduxStore.dispatch(setIsFrontCameraSelected(false));
        return { facingMode: CAMERA_FACING_MODE.BACK }; // environment
    }

    handleVideoStream = function (video_id, videoStream) {
        this.vidElement = document.getElementById(video_id);
        this.vidElement.srcObject = videoStream;
        const callConnected = reduxStore.getState().commonReducer.isRemoteParticipantJoined;
        if (callConnected) {
            this.vidElement.volume = 0;
        }
        this.vidElement.controls = false;
    };

    setCamLoading = function () {
        if (document.getElementById("callsettingVideo")) {
            document.getElementById("callsettingVideo").style.display = "none";
        }
        if (document.getElementById("callsettingVideo_vb")) {
            document.getElementById("callsettingVideo_vb").style.display = "none";
        }
        if (document.getElementById("showCamLoading")) {
            document.getElementById("showCamLoading").style.display = "flex";
        }
        if (document.getElementById("showCamLoading_vb")) {
            document.getElementById("showCamLoading_vb").style.display = "flex";
        }
    };
    unsetCamLoading = function () {
        if (document.getElementById("showCamLoading")) {
            document.getElementById("showCamLoading").style.display = "none";
        }
        if (document.getElementById("showCamLoading_vb")) {
            document.getElementById("showCamLoading_vb").style.display = "none";
        }
        if (document.getElementById("callsettingVideo")) {
            document.getElementById("callsettingVideo").style.display = "block";
            document.getElementById("callsettingVideo").classList.add("video");
        }
        if (document.getElementById("callsettingVideo_vb")) {
            document.getElementById("callsettingVideo_vb").style.display = "block";
            document.getElementById("callsettingVideo_vb").classList.add("video");
        }
    };
    hideCallSettingCamLoading = function () {
        if (document.getElementById("showCamLoading")) {
            document.getElementById("showCamLoading").style.display = "none";
        }
        if (document.getElementById("showCamLoading_vb")) {
            document.getElementById("showCamLoading_vb").style.display = "none";
        }
    };
    // It will ask for permission to access camera or audio devices.
    getUserMedia(fromWhere) {
        const { audioPermission, cameraPermission, currentCameraDevice, currentAudioDevice } = reduxStore.getState().callSetting;
        let constrains = {};
        // clearError function
        const errorElement = document.getElementById("callSettingError");
        if (errorElement) {
            errorElement.innerText = "";
        }
        this.removeMediaStream(); // remove twilio stream
        if (window.stream) {
            window.stream.getTracks().forEach((track) => {
                track.stop();
            });
        }

        let selectedAudioDevice = {};

        if (cameraPermission !== MEDIA_PERMISSIONS.BLOCKED) {
            constrains.video = this.getVideoSource(currentCameraDevice);
        }

        if (audioPermission !== MEDIA_PERMISSIONS.BLOCKED) {
            selectedAudioDevice = this.getAudioSource(currentAudioDevice);
            if (selectedAudioDevice?.deviceId) {
                reduxStore.dispatch(setCurrentAudioDevice(selectedAudioDevice));
            }
            constrains.audio = selectedAudioDevice?.deviceId ? { deviceId: { exact: selectedAudioDevice.deviceId } } : { deviceId: undefined };
        }

        //when others permission is not required.
        if (fromWhere === "audioOnly") {
            constrains.video = false;
        }
        if (fromWhere === "videoOnly") {
            constrains.audio = false;
        }
        if (constrains.video) {
            this.setCamLoading();
        }

        return navigator.mediaDevices
            .getUserMedia(constrains)
            .then((stream) => {
                console.info("Granted");
                if (constrains.video) {
                    this.unsetCamLoading();
                }
                if (audioPermission !== MEDIA_PERMISSIONS.BLOCKED) {
                    reduxStore.dispatch(setAudioPermission(MEDIA_PERMISSIONS.GRANTED));
                    reduxStore.dispatch(setIsMicExist(true));
                    // //disconnect earlier listener
                    this.stopTestingMic();
                    if (constrains.audio) {
                        this.startTestingMic(stream);
                    }
                }
                if (cameraPermission !== MEDIA_PERMISSIONS.BLOCKED) {
                    reduxStore.dispatch(setCameraPermission(MEDIA_PERMISSIONS.GRANTED));
                    this.isCameraExist = true;
                    reduxStore.dispatch(setIsCameraExist(true));
                }
                window.stream = stream;
                const { startLocalVideo } = reduxStore.getState().twilioEvents;
                if (!startLocalVideo) {
                    createMyLocalVideoTrack(constrains.video).then((videoTrack) => {
                        setLocalVideoTrackForCallSetting(videoTrack);
                        attachTrackInContainer(videoTrack, "callsettingVideo", 0, 0);
                        attachTrackInContainer(videoTrack, "callsettingVideo_vb", 0, 0);
                    });
                }
                //now permission is given so fetch connected device options
                this.getMediaDevices();
            })
            .catch((err) => {
                LogFirebase("CallSettings", "Error", err);
                console.log("getUserMedia error ==>", err);
                let camPermission = reduxStore.getState().callSetting.cameraPermission || MEDIA_PERMISSIONS.PROMPT;
                let audioPermission = reduxStore.getState().callSetting.audioPermission || MEDIA_PERMISSIONS.PROMPT;

                if (err.name == "NotFoundError" || err.name == "DevicesNotFoundError") {
                    this.isCameraExist = false;
                    this.isMicExist = false;
                    this.unsetCamLoading();
                    reduxStore.dispatch(setIsCameraExist(false));
                    reduxStore.dispatch(setIsMicExist(false));
                } else if (err.message == "Permission denied" || err.name == "NotAllowedError") {
                    this.unsetCamLoading();
                    if (camPermission != MEDIA_PERMISSIONS.GRANTED) {
                        camPermission = MEDIA_PERMISSIONS.BLOCKED;
                        micCameraPermissionDatatrackSend("cam");
                    }
                    if (audioPermission != MEDIA_PERMISSIONS.GRANTED) {
                        audioPermission = MEDIA_PERMISSIONS.BLOCKED;
                        micCameraPermissionDatatrackSend("mic");
                    }
                } else if (err?.name == "NotReadableError" || err.message == "Could not start video source") {
                    if (constrains.audio && !constrains.audio.deviceId) {
                        //mic permission was ask this time so fill the drop down
                        audioPermission = MEDIA_PERMISSIONS.GRANTED;
                        this.getMediaDevices();
                    }
                    const isCallSettingDrawerOpen = reduxStore.getState().commonReducer.isCallSettingDrawerOpen;
                    if (isCallSettingDrawerOpen) {
                        const errorElement = document.getElementById("callSettingError");
                        if (errorElement) {
                            errorElement.innerText =
                                Localize[PublicVariables?.currentLanguageObj?.languageCode]["failed_to_start_video_camera"] +
                                " " +
                                Localize[PublicVariables?.currentLanguageObj?.languageCode]["please_select_another_video_camera"];
                        }
                    }
                }

                LogFirebase("CallSettings", "CamPermission", camPermission);
                LogFirebase("CallSettings", "MicPermission", audioPermission);
                reduxStore.dispatch(setCameraPermission(camPermission));
                reduxStore.dispatch(setAudioPermission(audioPermission));
            });
    }

    // closing active stream
    closeActiveStream() {
        if (this.stream) {
            this.stream.getTracks().forEach((track) => {
                track.stop();
            });
            this.stream = null;
        }
    }
    // It will check the permission of device using navigator permissions query
    async checkPermission(deviceType) {
        try {
            const permission = await navigator.permissions.query({ name: deviceType });
            return permission.state;
        } catch (err) {
            let isMediaPermissionNotGranted = false;

            // For the Firefox browser, since it lacks support for query_permissions related to 'microphone' and 'camera', consider an alternative approach.
            // In Firefox, the media devices list may include devices with empty labels. We utilize this list to verify the presence of both audio and camera devices.
            if (err instanceof TypeError) {
                const devices = await navigator.mediaDevices.enumerateDevices();
                let kindOfDevice = "";
                if (deviceType === MEDIA_DEVICE_PERMISSION_QUERY_TYPE.MIC) {
                    kindOfDevice = INPUT_OUTPUT_DEVICE_TYPE.MIC;
                }
                if (deviceType === MEDIA_DEVICE_PERMISSION_QUERY_TYPE.CAMERA) {
                    kindOfDevice = INPUT_OUTPUT_DEVICE_TYPE.CAMERA;
                }
                // Without media permissions, browsers do not provide device IDs and/or labels.
                let deviceCount = 0;
                let deviceEmptyLabelCount = 0;

                devices.forEach((device) => {
                    if (device.kind === kindOfDevice) {
                        deviceCount++;
                        if (device.label === "") {
                            deviceEmptyLabelCount++;
                        }
                    }
                });

                isMediaPermissionNotGranted = deviceCount === deviceEmptyLabelCount && deviceCount !== 0;
            }

            return isMediaPermissionNotGranted ? MEDIA_PERMISSIONS.PROMPT : MEDIA_PERMISSIONS.GRANTED;
        }
    }

    // Retrieve a list of camera, audio, and speaker devices from all available devices.
    // async function to get all media devices
    // async function to get all media devices
    async getMediaDevices() {
        try {
            let allMediaDevices = await navigator.mediaDevices.enumerateDevices();
            const res = this.categorizeMediaDevices(allMediaDevices);
            reduxStore.dispatch(setCameraDevices(res.camera));
            reduxStore.dispatch(setAudioDevices(res.audio));
            reduxStore.dispatch(setSpeakerDevices(res.speaker));
            return res;
        } catch (err) {
            console.log("getMediaDevices err ==> ", err);
        }
    }

    // Starts testing the microphone by creating an AudioContext and connecting it to the MediaStream.
    startTestingMic(stream) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const context = new AudioContext();
        const source = context.createMediaStreamSource(stream);
        const analyser = context.createAnalyser();
        source.connect(analyser);
        this.stopTestingMic();
        analyser.fftSize = 1024;
        this.audioScriptNode = context.createScriptProcessor(2048, 1, 1);
        analyser.connect(this.audioScriptNode);
        this.audioScriptNode.connect(context.destination);
        this.audioScriptNode.onaudioprocess = function () {
            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            analyser.getByteFrequencyData(dataArray);
            const total = dataArray.reduce((acc, current) => acc + current, 0);
            const average = total / dataArray.length;
            const allMicBoxes = document.querySelectorAll(".microphone-level .mic-level-box");
            const activeBoxCount = Math.round(average / 10);
            allMicBoxes.forEach(function (box, index) {
                index < activeBoxCount ? box.classList.add("active") : box.classList.remove("active");
            });
        };
    }

    // Stops the audio script node.
    stopTestingMic() {
        if (this.audioScriptNode) {
            this.audioScriptNode.disconnect();
            this.audioScriptNode = null;
        }
    }

    // This function is used to categorize the media devices into different categories such as camera, audio, and speaker.
    categorizeMediaDevices(allMediaDevices = []) {
        const filteredDevices = {
            camera: [],
            audio: [],
            speaker: [],
        };

        allMediaDevices.forEach((device) => {
            if (device.kind === INPUT_OUTPUT_DEVICE_TYPE.CAMERA) {
                this.isCameraExist = true;
                reduxStore.dispatch(setIsCameraExist(true));
                filteredDevices.camera.push(device);
            } else if (device.kind === INPUT_OUTPUT_DEVICE_TYPE.MIC) {
                this.isMicExist = true;
                reduxStore.dispatch(setIsMicExist(true));
                filteredDevices.audio.push(device);
            } else if (device.kind === INPUT_OUTPUT_DEVICE_TYPE.SPEAKER) {
                filteredDevices.speaker.push(device);
            }
        });

        this.allMediaDevicesList = filteredDevices;
        return filteredDevices;
    }

    /**selects first back camera in case of notReadableerror */
    selectReadableErrorCam = function () {
        let foundFirst = false;
        const cameraDevicesList = reduxStore.getState().callSetting.cameraDevices;
        cameraDevicesList.forEach(function (device) {
            if (!foundFirst && device.label.toLowerCase().indexOf("back") > -1) {
                reduxStore.dispatch(setCurrentCameraDevice(device));
                foundFirst = true;
            }
        });
    };

    resetMediaDeviceService = function () {
        reduxStore.dispatch(setIsCallSettingDrawerOpen(false));
        this.removeCurrentMediaStream();
        //if user just opens call settings and close it without changing anything then publish vdo back
        const { startLocalVideo } = reduxStore.getState().twilioEvents;
        const callConnected = reduxStore.getState().commonReducer.isRemoteParticipantJoined;
        if (callConnected && startLocalVideo && flashService.shouldRestartLocalVideoTracksForIOSDeviceAndSafari()) {
            setTimeout(() => {
                if (reduxStore.getState().commonReducer.isRemoteParticipantJoined && reduxStore.getState().twilioEvents.startLocalVideo) {
                    restartLocalVideoTracks();
                }
            }, 700);
        }
    };
    removeMediaStream = function () {
        const { startLocalVideo } = reduxStore.getState().twilioEvents;
        if (!startLocalVideo) {
            const localVideoTrack = getLocalVideoTrackForCallSetting();
            localVideoTrack?.stop();
            localVideoTrack?.detach()?.forEach(function (detachedElement) {
                detachedElement.remove();
            });
            reduxStore.dispatch(setIsLocalVideoAttached(false));
            reduxStore.dispatch(setStartCallSettingLocalVideo(false));
            setLocalVideoTrackForCallSetting(null);
        }
    };
    removeCurrentMediaStream = function () {
        if (window.stream) {
            window.stream.getTracks().forEach((track) => {
                track.stop();
            });
        }
        this.removeMediaStream();
        this.stopTestingMic();
    };
    resetMediaDeviceOnCallEnd = function () {
        this.resetMediaDeviceService();
    };
}

export default new MediaDeviceService();
